import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { ReactComponent as CheckIcon } from '../../../assets/images/checkIcon.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/InfoIcon.svg';
import { ReactComponent as BlockedIcon } from '../../../assets/images/warningIcon.svg';
import EmptyPaymentsIcon from '../../../assets/svgComponents/EmptyPaymentsIcon';
import RewardInfoIcon from '../../../assets/svgComponents/RewardInfoIcon';
import WarningIcon from '../../../assets/svgComponents/WarningIcon';
import ColumnFilter from '../../../components/ColumnFilter/ColumnFilter';
import GlobalFilter from '../../../components/GlobalFilter/GlobalFilter';
import ReactTable from '../../../components/ReactTable/ReactTable';
import CustomPopover from '../../../components/shared/Popover/Popover';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../../constants/currenciesConstants';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/paginationDefaults';
import RewardTypes from '../../../constants/rewardTypes';
import { getPayments } from '../../../redux/actions/paymentActions';
import { useGetOrganizationPaymentPermissionsQuery } from '../../../redux/apis/organizationApi';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import PrePageFilters from '../PrePageFilters/PrePageFilters';

import DisputeModal from './DisputeModal/DisputeModal';
import IneligibleModal from './IneligibleModal/IneligibleModal';
import PaymentInformation from './PaymentInformation/PaymentInformation';

import styles from './PaymentsTable.module.scss';

const PAYMENT_STATUS = {
  paid: 'paid',
  disputed: 'disputed',
  resolved: 'resolved',
  ineligible: 'ineligible',
};

const PAYOUTS_PAGE_FILTERS = ['unpaid', 'ineligible', 'paid', 'all'];

const columnHeaders = (
  handleDisputeChange,
  isPaymentInfoAdded,
  isExperientialPaymentInfoAdded,
  handleSetReasons,
  t
) => [
  {
    Header: t('tableHeaders.id'),
    accessor: 'id',
    Filter: ColumnFilter,
    disableFilters: true,
  },
  {
    Header: t('tableHeaders.employer'),
    accessor: 'employer',
    minWidth: 130,
    Filter: ColumnFilter,
  },
  {
    Header: t('tableHeaders.openingTitle'),
    accessor: 'opening',
    minWidth: 150,
    Filter: ColumnFilter,
  },
  {
    Header: t('tableHeaders.candidate'),
    accessor: 'candidate',
    minWidth: 100,
    Filter: ColumnFilter,
  },
  {
    Header: t('tableHeaders.reward'),
    accessor: 'reward',
    minWidth: 80,
    Filter: ColumnFilter,
    Cell: ({ row, value }) =>
      row.original.rewardable_type === RewardTypes.MONETARY_REWARD ? (
        `${CURRENCIES[row.original.reward_currency ?? DEFAULT_CURRENCY]}${value}`
      ) : (
        <span>
          {value} <RewardInfoIcon />
        </span>
      ),
  },
  {
    Header: t('tableHeaders.dueDate'),
    accessor: 'due_date',
    minWidth: 80,
    Filter: ColumnFilter,
    Cell: ({ value }) => format(new Date(value), 'MM/dd/yyyy'),
  },
  {
    Header: t('tableHeaders.status'),
    accessor: 'status',
    minWidth: 100,
    Filter: ColumnFilter,
    Cell: ({ row, value }) => (
      <div className={styles.statusCellContent}>
        {value === 'ineligible' ? (
          <CustomPopover
            placement="top-end"
            delay={{
              show: 0,
              hide: 2000,
            }}
            id="ineligible-popover"
            className={styles.ineligiblePopover}
            text={
              <p className={styles.tooltip}>
                {t('ineligible.ineligibleToReceive')}
                <br />
                <span
                  className={`${styles.tooltip} ${styles.link}`}
                  onClick={() => handleSetReasons(row.original?.ineligible_reasons)}
                  role="presentation"
                >
                  {t('learnMore')}
                </span>
              </p>
            }
          >
            <div>
              <StatusBadge type="payment" value={value} icon={InfoIcon} />
            </div>
          </CustomPopover>
        ) : (
          <StatusBadge type="payment" value={value} />
        )}

        {row.original.disputed ? (
          <CustomPopover placement="top-end" id="dispute-popover" text={t('disputeMessages.paymentDisputed')}>
            <div>
              <StatusBadge type="payment" value={PAYMENT_STATUS.disputed} icon={WarningIcon} />
            </div>
          </CustomPopover>
        ) : (
          <button type="button" className={styles.disputeBtn} onClick={() => handleDisputeChange(row.original)}>
            {t('dispute.dispute')}
          </button>
        )}

        {value !== PAYMENT_STATUS.paid &&
          row.original.payment_initiated &&
          ((row.original.rewardable_type === 'MonetaryReward' && isPaymentInfoAdded) ||
          (row.original.rewardable_type === 'ExperientialReward' && isExperientialPaymentInfoAdded) ? (
            <StatusBadge type="payment" value={PAYMENT_STATUS.resolved} icon={CheckIcon} />
          ) : (
            <CustomPopover placement="top-end" id="resolved-popover" text={t('disputeMessages.paymentInfoRequired')}>
              <div>
                <StatusBadge type="payment" value="blocked" icon={BlockedIcon} />
              </div>
            </CustomPopover>
          ))}
      </div>
    ),
  },
];

const HEADERS_ACCESSORS = ['id', 'employer', 'opening', 'candidate', 'reward', 'due_date', 'status'];

const PaymentsTable = () => {
  const { t } = useTranslation('common');
  const userPayments = useSelector((state) => state.paymentsReducer.userPayments);
  const pageInfo = useSelector((state) => state.paymentsReducer.pageInfo);
  const isUserPaymentsLoading = useSelector((state) => state.paymentsReducer.isUserPaymentsLoading);
  const isPaymentInfoAdded = useSelector((state) => state.userReducer.user?.payment_info);
  const isExperientialPaymentInfoAdded = useSelector((state) => state.userReducer.user?.experiential_payment_info);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const { data: paymentPermissions } = useGetOrganizationPaymentPermissionsQuery(
    { organizationId: currentOrganization?.id },
    { skip: !currentOrganization?.id }
  );
  const hidePayThroughBoon = paymentPermissions?.payload?.hide_pay_through_boon;
  const [ineligibleReason, setIneligibleReason] = useState({ modal: false, reasons: [] });
  const [payoutPageFilter, setPayoutPageFilter] = useState('unpaid');

  const dispatch = useDispatch();

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState(null);

  useEffect(() => {
    dispatch(getPayments(page, perPage, searchTerm, payoutPageFilter));
  }, [page, perPage]);

  const nonResizableColumns = useMemo(
    () => (userPayments.length === 0 ? HEADERS_ACCESSORS : ['status']),
    [userPayments.length]
  );

  const [rowData, setRowData] = useState({});
  const [show, setShow] = useState(false);

  const handleOpenModal = useCallback((data) => {
    setRowData(data);
    setShow(true);
  }, []);

  const handleSetReasons = useCallback((primaryReasons) => {
    setIneligibleReason({
      modal: true,
      reasons: primaryReasons,
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    setIneligibleReason({
      modal: false,
      reasons: [],
    });
  }, []);

  const handleSearch = useCallback(
    (query) => {
      setPage(DEFAULT_PAGE);
      setSearchTerm(query);
      dispatch(getPayments(DEFAULT_PAGE, perPage, query, payoutPageFilter));
    },
    [perPage, payoutPageFilter]
  );

  const handlePageFiltersChange = useCallback((newFilter) => {
    setPayoutPageFilter(newFilter);
    dispatch(getPayments(DEFAULT_PAGE, DEFAULT_PER_PAGE, searchTerm, newFilter));
  });

  return (
    <div>
      <GlobalFilter
        placeholder={t('placeholders.searchPayments')}
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        setPage={setPage}
      />
      <PrePageFilters filters={PAYOUTS_PAGE_FILTERS} state={payoutPageFilter} setState={handlePageFiltersChange} />
      {!hidePayThroughBoon && (!isPaymentInfoAdded || !isExperientialPaymentInfoAdded) && (
        <PaymentInformation isExperientialPaymentInfoAdded={isExperientialPaymentInfoAdded} />
      )}
      <ReactTable
        data={userPayments}
        columns={columnHeaders(
          handleOpenModal,
          isPaymentInfoAdded,
          isExperientialPaymentInfoAdded,
          handleSetReasons,
          t
        )}
        onSelectRow={() => {}}
        selectedItems={[]}
        defaultSortingState={[]}
        setSortingState={() => {}}
        defaultStyling={{ header_height: 'header-height' }}
        disableColumnResizing={nonResizableColumns}
        defaultFeildSize={1.09}
        pageInfo={pageInfo}
        setPage={setPage}
        setPerPage={setPerPage}
        emptyIcon={EmptyPaymentsIcon}
        emptyTitle={t('payments.noPayments')}
        loading={isUserPaymentsLoading}
        perPage={perPage}
        showTooltip
      />
      <DisputeModal rowData={rowData} show={show} setShow={setShow} />
      <IneligibleModal
        showModal={ineligibleReason.modal}
        reasons={ineligibleReason.reasons}
        closeModal={handleCloseModal}
      />
    </div>
  );
};

export default PaymentsTable;
